import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Project from 'models/Project';
import {
  START_LOADING,
  STOP_LOADING,
  SET_STEP,
  SET_ERRORS,
} from 'reducers/createProjectFormReducer';
import { validateForm } from 'utils/formValidation';
import { steps } from 'utils/steps';

/**
 * Returns the first step containing a field with an error.
 *
 * @param {Object} validationResponse - An object containing validation error messages.
 * @param {Object} steps - An object containing step definitions.
 * @return {Object | undefined} - The step containing the first field with an error, or undefined if not found.
 */
const getFirstFieldWithError = (validationResponse) => {
  // Check if input arguments are valid objects
  if (
    !validationResponse ||
    typeof validationResponse !== 'object' ||
    !steps ||
    typeof steps !== 'object'
  ) {
    return undefined;
  }

  const firstFieldWithError = Object.keys(validationResponse)[0];

  if (!firstFieldWithError) {
    return undefined;
  }

  for (const step in steps) {
    if (steps[step].fields.includes(firstFieldWithError)) {
      return parseInt(step);
    }
  }
};

/**
 * Custom hook for handling form submission logic
 * @param {Object} param0 - Destructured object containing state, dispatch, steps, and businessId properties
 * @param {Object} param0.state - State object holding form data
 * @param {Function} param0.dispatch - Function to dispatch actions to update state
 * @param {Object} param0.steps - Object representing steps in the form
 * @param {string} param0.businessId - ID of the associated business
 * @returns {Object} Object with handleSubmitButtonClick function
 */
const useSubmit = ({ state, dispatch, businessId }) => {
  const navigate = useNavigate();
  const submittingRef = useRef(false);

  /**
   * Handles submit button click by validating form data, creating the project in the database, and navigating to success or error page
   * @returns {Promise<void>} A promise resolving once form submission is complete
   */
  const handleSubmitButtonClick = async () => {
    // Guard against double-submission from rapid Enter presses
    // before React re-renders the disabled button.
    if (submittingRef.current || state.loading) return;
    submittingRef.current = true;
    dispatch({ type: START_LOADING });

    try {
      // Validate form data.
      const validationResponse = validateForm({ data: state.data });

      // Handle validation errors.
      if (validationResponse) {
        // Update errors state.
        dispatch({ type: SET_ERRORS, payload: { errors: validationResponse } });

        // Update step to first step with error.
        dispatch({
          type: SET_STEP,
          payload: { step: getFirstFieldWithError(validationResponse) },
        });

        return;
      }

      try {
        await Project.createInDB(state.data, businessId);
        navigate('/success');
      } catch (error) {
        /*
          Server side errors can be handled here, and step can be changed to corresponding field.
          Example: E-mail already in use ? step = 1 && errors = { ...errors, email: 'E-mail in gebruik, log in of gebruik ander email adres' };
        */
        navigate('/error');
      }
    } finally {
      dispatch({ type: STOP_LOADING });
      submittingRef.current = false;
    }
  };

  return {
    handleSubmitButtonClick,
  };
};

export default useSubmit;
